import { KEYBOARD_SHORTCUT_SETTINGS_ROUTE, SHELL_SETTINGS_ROUTE } from './../../common/routes'
import { noop, navigateToUrl } from '../../common/helpers'
import { openNewTabFor } from '../../common/dom-helpers'
import { environment } from '../../environments'
import { getFeatureFlagValue } from '../../services/feature-flags'
import { FeatureFlagsVariations } from '../../services/feature-flags/models'
import { asTranslationKey } from '../../common/translate-helpers/i18n-utils'
import { getShellAuth, getShellNamespaces, getShellAnalytics } from '../../common/shell-api-helpers'
import type { MenuItem, SeparatorMenuItem } from '../menu-item/menu-item-models'
import { MenuItemType } from '../menu-item/menu-item-models'
import type { ResolveNamespace } from '../../services/namespaces/resolve-namespace'
import { RESOLVE_NAMESPACE } from '../../services/namespaces/resolve-namespace'
import { html } from 'lit-element'
import { setupUserReport } from '../user-report'
import { getExternalInterface } from '../../services/external-interface/external-interface-adapter'
import { isGoToMeetingCustomer } from '../../services/identity/entitlement'
import { CHATBOT_NAMESPACE, type ChatbotNamespace } from '../../services/namespaces/chatbot-namespace'

const separatorItem: SeparatorMenuItem = {
  type: MenuItemType.SEPARATOR,
}

const separatorItemForIntegration: SeparatorMenuItem = {
  type: MenuItemType.SEPARATOR,
  dataTest: 'integration-separator-item',
  isVisible: () => getExternalInterface().isIntegration,
}

export const menuItems: readonly MenuItem[] = [
  separatorItem,
  {
    href: '#',
    type: MenuItemType.NORMAL,
    executeFunction: () => {
      openNewTabFor(environment().profileUrl)
    },
    labelKey: asTranslationKey('Edit Profile'),
    isExternalLink: true,
    dataTest: 'edit-profile',
  },
  separatorItem,
  {
    type: MenuItemType.NORMAL,
    href: '#',
    executeFunction: noop,
    labelKey: asTranslationKey('About'),
    dataTest: 'about',
    isVisible: () => false, // The version is displayed in the last item of avatar popover menu. When chameleon supports a way to implement a sub menu we should enable this. https://jira.ops.expertcity.com/browse/SCORE-1217
  },
  {
    type: MenuItemType.CUSTOM,
    render: () => html`<goto-avatar-update-menu-item></goto-avatar-update-menu-item>`,
    dataTest: 'update',
  },
  {
    type: MenuItemType.NORMAL,
    href: KEYBOARD_SHORTCUT_SETTINGS_ROUTE,
    executeFunction: () => {
      navigateToUrl(`${KEYBOARD_SHORTCUT_SETTINGS_ROUTE}`)
    },
    labelKey: asTranslationKey('Keyboard shortcuts'),
    isVisible: () =>
      !!getFeatureFlagValue(FeatureFlagsVariations.SHELL_HOTKEY_SETTINGS) && !getExternalInterface().isIntegration,
    dataTest: 'keyboard-shortcuts',
  },
  {
    type: MenuItemType.NORMAL,
    href: SHELL_SETTINGS_ROUTE,
    executeFunction: () => {
      navigateToUrl(`${SHELL_SETTINGS_ROUTE}`)
    },
    labelKey: asTranslationKey('Settings'),
    isVisible: () => true,
    dataTest: 'settings',
  },
  {
    type: MenuItemType.NORMAL,
    href: '#',
    executeFunction: () => {
      const resolveNamespace = getShellNamespaces().retrieve<ResolveNamespace>(RESOLVE_NAMESPACE)
      resolveNamespace.commands.openCreateIncidentDialog.execute()
      getShellAnalytics().track('GoTo > Open Ticket Resolve', { action: 'Click', event: 'Open', from: 'Topbar' })
    },
    labelKey: asTranslationKey('Contact my admin'),
    isVisible: () => {
      const resolveNamespace = getShellNamespaces().retrieve<ResolveNamespace>(RESOLVE_NAMESPACE)
      return resolveNamespace.commands.openCreateIncidentDialog.isHandled()
    },
    dataTest: 'createResolveIncident',
  },
  {
    type: MenuItemType.NORMAL,
    href: '#',
    executeFunction: () => {
      const assistantNamespace = getShellNamespaces().retrieve<ChatbotNamespace>(CHATBOT_NAMESPACE)
      assistantNamespace.commands.openChatbot.execute()
    },
    labelKey: asTranslationKey('Chatbot'),
    isVisible: () => {
      const assistantNamespace = getShellNamespaces().retrieve<ChatbotNamespace>(CHATBOT_NAMESPACE)
      return assistantNamespace.commands.openChatbot.isHandled()
    },
    dataTest: 'chatbot',
  },
  separatorItemForIntegration,
  {
    type: MenuItemType.NORMAL,
    href: '#',
    executeFunction: () => {
      openNewTabFor(environment().helpUrl)
    },
    labelKey: asTranslationKey('Get help'),
    isExternalLink: true,
    isVisible: () => getExternalInterface().isIntegration,
    dataTest: 'help',
  },
  {
    type: MenuItemType.NORMAL,
    href: '#',
    executeFunction: () => {
      openNewTabFor(environment().tutorialsAndVideosUrl)
    },
    labelKey: asTranslationKey('Tutorials and videos'),
    isExternalLink: true,
    isVisible: () => getExternalInterface().isIntegration,
    dataTest: 'tutorials-and-videos',
  },
  {
    type: MenuItemType.NORMAL,
    href: '#',
    executeFunction: () => {
      openNewTabFor(environment().communityForumUrl)
    },
    labelKey: asTranslationKey('Community forum'),
    isExternalLink: true,
    isVisible: () => getExternalInterface().isIntegration,
    dataTest: 'community-forum',
  },
  {
    type: MenuItemType.NORMAL,
    href: '#',
    executeFunction: () => {
      window.open('https://logmeinvoc.iad1.qualtrics.com/jfe/form/SV_39obCEnQHAKSR7g', '_blank', 'noreferrer')
    },
    isExternalLink: true,
    labelKey: asTranslationKey('Give feedback'),
    isVisible: () => isGoToMeetingCustomer() && getExternalInterface().isIntegration,
    dataTest: 'feedbackG2M',
  },
  {
    type: MenuItemType.NORMAL,
    href: '#',
    executeFunction: () => {
      const { openFeedbackDialog } = setupUserReport(window)
      openFeedbackDialog()
    },
    labelKey: asTranslationKey('Give feedback'),
    isVisible: () => !isGoToMeetingCustomer() && getExternalInterface().isIntegration,
    dataTest: 'feedback',
  },
  separatorItem,
  {
    type: MenuItemType.NORMAL,
    href: '#',
    executeFunction: () => getShellAuth().logout(),
    labelKey: asTranslationKey('Sign out'),
    dataTest: 'logout',
  },
]
